import { createServerFn } from '@tanstack/react-start';
import { defineQuery } from 'groq';

import {
  footerLegalDocumentIds,
  getFooterLegalLinks,
  getLegalDocumentPath,
  type LegalLink,
} from '@/utils/legal-links';

import { createSanityClient } from './client';

export const legalDocumentProjection = /* groq */ `{
  title,
  lastUpdated,
  "body": body[] {
    _key,
    _type,
    _type == "block" => {
      children[] {
        _key,
        _type,
        marks,
        text
      },
      markDefs[] {
        _key,
        _type,
        href
      },
      style,
      listItem,
      level
    },
    _type == "legalTable" => {
      rows[] {
        _key,
        cells[] {
          _key,
          text
        }
      }
    }
  }
}`;

const publishedLegalDocumentIdsQuery = defineQuery(
  `*[_id in $documentIds]._id`,
);

const sitemapLegalDocumentsQuery = defineQuery(`*[
  _id in $documentIds
] {
  _id,
  lastUpdated
}`);

type SitemapLegalDocument = {
  _id: string;
  lastUpdated: string | null;
};

type SitemapLegalPage = {
  path: string;
  lastUpdated: string | null;
};

/** Links for legal documents the current Sanity perspective can read. */
async function getPublishedLegalLinks(): Promise<LegalLink[]> {
  const client = createSanityClient();

  try {
    const publishedDocumentIds = await client.fetch(
      publishedLegalDocumentIdsQuery,
      { documentIds: footerLegalDocumentIds },
    );

    return getFooterLegalLinks(publishedDocumentIds);
  } catch (error) {
    console.error('Failed to load published Sanity legal documents', error);
    return [];
  }
}

/** Published legal pages and the editorial date shown on each page. */
export async function getSitemapLegalPages(): Promise<SitemapLegalPage[]> {
  const client = createSanityClient();

  try {
    const documents = await client.fetch<SitemapLegalDocument[]>(
      sitemapLegalDocumentsQuery,
      { documentIds: footerLegalDocumentIds },
    );

    return documents.flatMap((document) => {
      const path = getLegalDocumentPath(document._id);

      if (!path) {
        return [];
      }

      return [{ path, lastUpdated: document.lastUpdated }];
    });
  } catch (error) {
    console.error('Failed to load Sanity legal pages for the sitemap', error);
    return [];
  }
}

/** Public server function: it only reads which legal documents are published. */
export const getLegalLinks = createServerFn({ method: 'GET' }).handler(() =>
  getPublishedLegalLinks(),
);
